import { MouseEvent, ReactElement, ReactNode, useMemo, useState } from 'react'
import { SLIGHTLY_OVERUTILIZED_THRESHOLD } from '@constants'
import convert from 'convert'
import { Box, Button, IconButton, Popover, useTheme } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { EmptyState } from '@ui2/feedback'
import { MaterialSymbol } from '@ui2/icons'
import {
  getLoadDurationLabel,
  getMinLoadDurationFromCombination,
} from '../../../../utils/loadDuration'
import LoadCaseAbbreviation from '../CheckInformation/LoadCaseAbbreviation'

type DetailItem = {
  label: ReactNode
  value: ReactNode
}

type ResultRow = {
  id: string
  label: ReactNode
  value: ReactNode
  details: DetailItem[]
  utilization?: number
}

interface Props {
  checksOfPosition: StiffeningSegmentCheck[]
  children: (sections: { design: ReactNode; nachweis: ReactNode }) => ReactElement
}

const numberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const compactNumberFormatter = new Intl.NumberFormat('de-DE', {
  notation: 'compact',
  compactDisplay: 'short',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const fmt = (value: number) => numberFormatter.format(value)

const fmtCompact = (value: number) => compactNumberFormatter.format(value)

const toNPerMm = (value: number) => `${fmtCompact(convert(value, 'millimeter').to('meter'))} N/mm`

const toKn = (value: number) => `${fmtCompact(convert(value, 'newton').to('kilonewton'))} kN`

const toKnPerM = (value: number) => `${fmtCompact(convert(value, 'newton').to('kilonewton'))} kN/m`

const toMm = (value: number) => `${fmtCompact(convert(value, 'meter').to('millimeter'))} mm`

const utilizationCellSx = (
  value: number | undefined,
  theme: Theme,
): { background: string; color: string; fontWeight: number } | null => {
  if (value === undefined) return null
  if (value >= SLIGHTLY_OVERUTILIZED_THRESHOLD) {
    return {
      background: theme.results.utilization.overutilized.light,
      color: theme.palette.text.primary,
      fontWeight: 600,
    }
  }
  if (value >= 1.0) {
    return {
      background: theme.results.utilization.slightlyOverutilized.light,
      color: theme.palette.text.primary,
      fontWeight: 600,
    }
  }
  return null
}

const DetailPopover = ({
  row,
  anchorEl,
  onClose,
}: {
  row: ResultRow | null
  anchorEl: HTMLElement | null
  onClose: () => void
}): ReactElement => {
  const theme = useTheme()
  return (
    <Popover
      open={Boolean(row && anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      PaperProps={{
        sx: {
          width: 320,
          borderRadius: 1.25,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
        },
        onClick: onClose,
      }}
    >
      <Box sx={{ px: 1.5, py: 1.25 }}>
        <Box
          sx={{
            fontFamily: 'Roboto',
            fontSize: 12.5,
            fontWeight: 600,
            color: theme.palette.text.primary,
            mb: 1,
          }}
        >
          {row?.label ?? 'Details'}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.625 }}>
          {row?.details.length ? (
            row.details.map((detail: DetailItem, index: number) => (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(130px, 1fr) minmax(90px, auto)',
                  gap: 1,
                  alignItems: 'baseline',
                  fontSize: 12,
                  fontFamily: 'Roboto',
                }}
              >
                <Box sx={{ color: theme.palette.text.secondary }}>{detail.label}</Box>
                <Box sx={{ color: theme.palette.text.primary }}>{detail.value}</Box>
              </Box>
            ))
          ) : (
            <EmptyState sx={{ py: 0, px: 0, color: 'text.secondary' }}>
              Keine Detailwerte verfügbar
            </EmptyState>
          )}
        </Box>
        <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="text" size="small" onClick={onClose} sx={{ textTransform: 'none' }}>
            Schließen
          </Button>
        </Box>
      </Box>
    </Popover>
  )
}

const ResultsTable = ({
  rows,
  valueHeader,
}: {
  rows: ResultRow[]
  valueHeader: ReactNode
}): ReactElement => {
  const theme = useTheme()
  const [detailsRow, setDetailsRow] = useState<ResultRow | null>(null)
  const [detailsAnchorEl, setDetailsAnchorEl] = useState<HTMLElement | null>(null)

  const closeDetails = () => {
    setDetailsRow(null)
    setDetailsAnchorEl(null)
  }

  return (
    <Box sx={{ pt: 0.75 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(130px,1fr) 90px 28px',
          gap: 0.75,
          fontSize: 11,
          color: theme.palette.text.secondary,
          pb: 0.5,

          alignItems: 'center',
        }}
      >
        <Box>Typ</Box>
        <Box sx={{ textAlign: 'right' }}>{valueHeader}</Box>
        <Box />
      </Box>
      {rows.map(row => {
        const cellSx = utilizationCellSx(row.utilization, theme)
        return (
          <Box
            key={row.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(130px,1fr) 90px 28px',
              gap: 0.75,
              alignItems: 'center',
              py: 0.625,
              borderTop: `1px solid ${theme.palette.divider}`,
              fontSize: 12,
              fontFamily: 'Roboto',
            }}
          >
            <Box sx={{ color: theme.palette.text.primary }}>{row.label}</Box>
            <Box
              sx={{
                justifySelf: 'end',

                color: cellSx?.color ?? theme.palette.text.primary,
                background: cellSx?.background ?? 'transparent',
                fontWeight: cellSx?.fontWeight,
                borderRadius: 0.5,
                px: 0.375,
              }}
            >
              {row.value}
            </Box>
            <Box sx={{ display: 'inline-flex', justifyContent: 'flex-end' }}>
              <IconButton
                size="small"
                onClick={(e: MouseEvent<HTMLElement>) => {
                  setDetailsRow(row)
                  setDetailsAnchorEl(e.currentTarget)
                }}
                title="Details"
              >
                <MaterialSymbol icon="info" size={16} />
              </IconButton>
            </Box>
          </Box>
        )
      })}
      <DetailPopover row={detailsRow} anchorEl={detailsAnchorEl} onClose={closeDetails} />
    </Box>
  )
}

const ShearFlowColumns = ({
  designShearFlow,
  loadDuration,
  lcc,
  kModInside,
  kModOutside,
}: {
  designShearFlow?: number
  loadDuration?: LoadDurationType
  lcc?: LoadCaseCombination
  kModInside?: number
  kModOutside?: number
}): ReactElement => {
  const theme = useTheme()

  const cols =
    'minmax(90px,0.8fr) minmax(64px,0.6fr) minmax(82px,0.7fr) minmax(82px,0.7fr) minmax(180px,1.8fr)'
  const valueOrNa = (value: ReactNode | undefined) => value ?? 'n/a'
  const lccValue = lccDetails(lcc)[0]?.value

  return (
    <Box sx={{ pt: 0.75 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: cols,
          gap: 0.75,
          fontSize: 11,
          color: theme.palette.text.secondary,
          pb: 0.5,

          alignItems: 'center',
        }}
      >
        <Box>
          s<sub>v0d</sub>
        </Box>
        <Box>KLED</Box>
        <Box>
          k<sub>mod</sub> innen
        </Box>
        <Box>
          k<sub>mod</sub> außen
        </Box>
        <Box>Lastfallkombination</Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: cols,
          gap: 0.75,
          alignItems: 'center',
          py: 0.625,
          borderTop: `1px solid ${theme.palette.divider}`,
          fontSize: 12,

          color: theme.palette.text.primary,
        }}
      >
        <Box>
          {valueOrNa(designShearFlow !== undefined ? toNPerMm(designShearFlow) : undefined)}
        </Box>
        <Box>{valueOrNa(loadDuration ? getLoadDurationLabel(loadDuration) : undefined)}</Box>
        <Box>{valueOrNa(kModInside !== undefined ? fmt(kModInside) : undefined)}</Box>
        <Box>{valueOrNa(kModOutside !== undefined ? fmt(kModOutside) : undefined)}</Box>
        <Box sx={{ fontFamily: 'Roboto', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {valueOrNa(lccValue)}
        </Box>
      </Box>
    </Box>
  )
}

const lccDetails = (lcc?: LoadCaseCombination): DetailItem[] =>
  lcc?.data
    ? [
        {
          label: 'Lastfallkombination',
          value: (
            <>
              {lcc.data.map(([v1, v2, loadCase], i) => (
                <span key={i}>
                  {v1}*{v2}*
                  <LoadCaseAbbreviation combination={loadCase} />
                  {i !== lcc.data.length - 1 && ' + '}
                </span>
              ))}
            </>
          ),
        },
      ]
    : []

const buildFallbackDetails = (): StiffeningSegmentCheckDetails => ({
  b_net: 0,
  f_v0d_thrust_plate_combined: 0,
  f_v0d_shear_plate_combined: 0,
  f_v0d_buckling_combined: 0,
  design_shear_load: 0,
  stiffness: 0,
  displacement: 0,
  max_allowed_displacement: 0,
})

const Results2StiffeningCheckResults = ({ checksOfPosition, children }: Props): ReactElement => {
  const maxUtilizationCheck = useMemo(() => {
    return checksOfPosition.reduce(
      (maxCheck, currentCheck) =>
        !maxCheck || currentCheck.max_utilization > maxCheck.max_utilization
          ? currentCheck
          : maxCheck,
      null as StiffeningSegmentCheck | null,
    )
  }, [checksOfPosition])

  const minLoadDuration = useMemo(
    () => getMinLoadDurationFromCombination(maxUtilizationCheck?.decisive_load_case_combination),
    [maxUtilizationCheck],
  )

  const { utilizationFastener, utilizationPlate, utilizationBuckling, checkDetails } =
    useMemo(() => {
      const insideResistanceData = maxUtilizationCheck?.resistance_data.inside_plate_data
      const outsideResistanceData = maxUtilizationCheck?.resistance_data.outside_plate_data

      let utilizationFastener: number | undefined
      let utilizationPlate: number | undefined
      let utilizationBuckling: number | undefined
      let checkDetails: StiffeningSegmentCheckDetails | undefined

      if (insideResistanceData && outsideResistanceData) {
        const smallerSideFactor = maxUtilizationCheck?.resistance_data.smaller_side_factor

        const insideFastenerResistance = insideResistanceData.f_v0d_shear_plate
        const outsideFastenerResistance = outsideResistanceData.f_v0d_shear_plate
        const combinedFastenerResistance =
          Math.max(insideFastenerResistance, outsideFastenerResistance) +
          (smallerSideFactor || 0) * Math.min(insideFastenerResistance, outsideFastenerResistance)
        utilizationFastener = maxUtilizationCheck?.s_v_0 / combinedFastenerResistance

        const insidePlateResistance = insideResistanceData.f_v0d_thrust_plate
        const outsidePlateResistance = outsideResistanceData.f_v0d_thrust_plate
        const combinedPlateResistance =
          Math.max(insidePlateResistance, outsidePlateResistance) +
          (smallerSideFactor || 0) * Math.min(insidePlateResistance, outsidePlateResistance)
        utilizationPlate = maxUtilizationCheck?.s_v_0 / combinedPlateResistance

        const insideBucklingResistance = insideResistanceData.f_v0d_buckling
        const outsideBucklingResistance = outsideResistanceData.f_v0d_buckling
        const combinedBucklingResistance =
          Math.max(insideBucklingResistance, outsideBucklingResistance) +
          (smallerSideFactor || 0) * Math.min(insideBucklingResistance, outsideBucklingResistance)
        utilizationBuckling = maxUtilizationCheck?.s_v_0 / combinedBucklingResistance

        checkDetails = {
          smaller_side_factor: smallerSideFactor,
          f_v0d_thrust_plate_inside: insidePlateResistance,
          f_v0d_thrust_plate_outside: outsidePlateResistance,
          f_v0d_thrust_plate_combined: combinedPlateResistance,
          c_inside: insideResistanceData.c,
          c_outside: outsideResistanceData.c,
          f_v0d_shear_plate_inside: insideFastenerResistance,
          f_v0d_shear_plate_outside: outsideFastenerResistance,
          f_v0d_shear_plate_combined: combinedFastenerResistance,
          b_net: maxUtilizationCheck?.resistance_data.b_net,
          k_v2_inside: insideResistanceData.k_v2,
          k_v2_outside: outsideResistanceData.k_v2,
          f_v0d_buckling_inside: insideBucklingResistance,
          f_v0d_buckling_outside: outsideBucklingResistance,
          f_v0d_buckling_combined: combinedBucklingResistance,
          displacement: maxUtilizationCheck?.displacement,
          max_allowed_displacement: maxUtilizationCheck?.max_allowed_displacement,
          design_shear_load: maxUtilizationCheck?.design_shear_load,
          stiffness: maxUtilizationCheck?.stiffness,
        }
      } else if (insideResistanceData || outsideResistanceData) {
        const data = insideResistanceData ?? outsideResistanceData
        const side = insideResistanceData ? 'inside' : 'outside'
        if (!data)
          return { utilizationFastener, utilizationPlate, utilizationBuckling, checkDetails }

        utilizationFastener = maxUtilizationCheck?.s_v_0 / data.f_v0d_shear_plate
        utilizationPlate = maxUtilizationCheck?.s_v_0 / data.f_v0d_thrust_plate
        utilizationBuckling = maxUtilizationCheck?.s_v_0 / data.f_v0d_buckling

        checkDetails = {
          f_v0d_thrust_plate_inside: side === 'inside' ? data.f_v0d_thrust_plate : undefined,
          f_v0d_thrust_plate_outside: side === 'outside' ? data.f_v0d_thrust_plate : undefined,
          f_v0d_thrust_plate_combined: data.f_v0d_thrust_plate,
          c_inside: side === 'inside' ? data.c : undefined,
          c_outside: side === 'outside' ? data.c : undefined,
          f_v0d_shear_plate_inside: side === 'inside' ? data.f_v0d_shear_plate : undefined,
          f_v0d_shear_plate_outside: side === 'outside' ? data.f_v0d_shear_plate : undefined,
          f_v0d_shear_plate_combined: data.f_v0d_shear_plate,
          b_net: maxUtilizationCheck?.resistance_data.b_net,
          k_v2_inside: side === 'inside' ? data.k_v2 : undefined,
          k_v2_outside: side === 'outside' ? data.k_v2 : undefined,
          f_v0d_buckling_inside: side === 'inside' ? data.f_v0d_buckling : undefined,
          f_v0d_buckling_outside: side === 'outside' ? data.f_v0d_buckling : undefined,
          f_v0d_buckling_combined: data.f_v0d_buckling,
          displacement: maxUtilizationCheck?.displacement,
          max_allowed_displacement: maxUtilizationCheck?.max_allowed_displacement,
          design_shear_load: maxUtilizationCheck?.design_shear_load,
          stiffness: maxUtilizationCheck?.stiffness,
        }
      }

      return { utilizationFastener, utilizationPlate, utilizationBuckling, checkDetails }
    }, [maxUtilizationCheck])

  if (!maxUtilizationCheck) {
    return children({
      design: <EmptyState sx={{ py: 2 }}>Kein Bemessungsschubfluss verfügbar</EmptyState>,
      nachweis: (
        <EmptyState sx={{ py: 2 }}>Keine Nachweise für diese Wandscheibe verfügbar</EmptyState>
      ),
    })
  }

  const details = checkDetails || buildFallbackDetails()

  const checkRows: ResultRow[] = [
    {
      id: 'fastener',
      label: 'Verbindungsmittel',
      value: fmt(utilizationFastener || 0),
      utilization: utilizationFastener || 0,
      details: [
        ...(details.smaller_side_factor
          ? [
              {
                label: 'Faktor bei unterschiedlicher Beplankung',
                value: fmtCompact(details.smaller_side_factor),
              },
            ]
          : []),
        ...(details.c_inside
          ? [
              {
                label: (
                  <>
                    <span>c</span>
                    <sub>innen</sub>
                  </>
                ),
                value: fmtCompact(details.c_inside),
              },
            ]
          : []),
        ...(details.c_outside
          ? [
              {
                label: (
                  <>
                    <span>c</span>
                    <sub>außen</sub>
                  </>
                ),
                value: fmtCompact(details.c_outside),
              },
            ]
          : []),
        ...(details.f_v0d_shear_plate_inside
          ? [
              {
                label: (
                  <>
                    <span>f</span>
                    <sub>v0d,innen</sub>
                  </>
                ),
                value: toNPerMm(details.f_v0d_shear_plate_inside),
              },
            ]
          : []),
        ...(details.f_v0d_shear_plate_outside
          ? [
              {
                label: (
                  <>
                    <span>f</span>
                    <sub>v0d,außen</sub>
                  </>
                ),
                value: toNPerMm(details.f_v0d_shear_plate_outside),
              },
            ]
          : []),
        {
          label: (
            <>
              <span>f</span>
              <sub>v0d,ges</sub>
            </>
          ),
          value: toNPerMm(details.f_v0d_shear_plate_combined),
        },
      ],
    },
    {
      id: 'buckling',
      label: 'Beulen',
      value: fmt(utilizationBuckling || 0),
      utilization: utilizationBuckling || 0,
      details: [
        ...(details.smaller_side_factor
          ? [
              {
                label: 'Faktor bei unterschiedlicher Beplankung',
                value: fmtCompact(details.smaller_side_factor),
              },
            ]
          : []),
        {
          label: (
            <>
              <span>b</span>
              <sub>net</sub>
            </>
          ),
          value: fmtCompact(details.b_net),
        },
        ...(details.k_v2_inside
          ? [
              {
                label: (
                  <>
                    <span>k</span>
                    <sub>v2,innen</sub>
                  </>
                ),
                value: fmtCompact(details.k_v2_inside),
              },
            ]
          : []),
        ...(details.k_v2_outside
          ? [
              {
                label: (
                  <>
                    <span>k</span>
                    <sub>v2,außen</sub>
                  </>
                ),
                value: fmtCompact(details.k_v2_outside),
              },
            ]
          : []),
        ...(details.f_v0d_buckling_inside
          ? [
              {
                label: (
                  <>
                    <span>f</span>
                    <sub>v0d,innen</sub>
                  </>
                ),
                value: toNPerMm(details.f_v0d_buckling_inside),
              },
            ]
          : []),
        ...(details.f_v0d_buckling_outside
          ? [
              {
                label: (
                  <>
                    <span>f</span>
                    <sub>v0d,außen</sub>
                  </>
                ),
                value: toNPerMm(details.f_v0d_buckling_outside),
              },
            ]
          : []),
        {
          label: (
            <>
              <span>f</span>
              <sub>v0d,ges</sub>
            </>
          ),
          value: toNPerMm(details.f_v0d_buckling_combined),
        },
      ],
    },
    {
      id: 'plate',
      label: 'Platte',
      value: fmt(utilizationPlate || 0),
      utilization: utilizationPlate || 0,
      details: [
        ...(details.smaller_side_factor
          ? [
              {
                label: 'Faktor bei unterschiedlicher Beplankung',
                value: fmtCompact(details.smaller_side_factor),
              },
            ]
          : []),
        ...(details.f_v0d_thrust_plate_inside
          ? [
              {
                label: (
                  <>
                    <span>f</span>
                    <sub>v0d,innen</sub>
                  </>
                ),
                value: toNPerMm(details.f_v0d_thrust_plate_inside),
              },
            ]
          : []),
        ...(details.f_v0d_thrust_plate_outside
          ? [
              {
                label: (
                  <>
                    <span>f</span>
                    <sub>v0d,außen</sub>
                  </>
                ),
                value: toNPerMm(details.f_v0d_thrust_plate_outside),
              },
            ]
          : []),
        {
          label: (
            <>
              <span>f</span>
              <sub>v0d,ges</sub>
            </>
          ),
          value: toNPerMm(details.f_v0d_thrust_plate_combined),
        },
      ],
    },
    {
      id: 'displacement',
      label: 'Verformungen',
      value: fmt(maxUtilizationCheck.max_utilization_displacement || 0),
      utilization: maxUtilizationCheck.max_utilization_displacement || 0,
      details: [
        {
          label: (
            <>
              <span>F</span>
              <sub>v,d</sub>
            </>
          ),
          value: toKn(details.design_shear_load),
        },
        {
          label: (
            <>
              <span>K</span>
              <sub>ges</sub>
            </>
          ),
          value: toKnPerM(details.stiffness),
        },
        {
          label: (
            <>
              <span>u</span>
              <sub>d</sub>
            </>
          ),
          value: toMm(details.displacement),
        },
        {
          label: (
            <>
              <span>u</span>
              <sub>zul,GZT</sub>
            </>
          ),
          value: toMm(details.max_allowed_displacement),
        },
      ],
    },
  ]

  return children({
    design: (
      <ShearFlowColumns
        designShearFlow={maxUtilizationCheck.s_v_0}
        loadDuration={minLoadDuration as LoadDurationType | undefined}
        lcc={maxUtilizationCheck.decisive_load_case_combination}
        kModInside={maxUtilizationCheck.resistance_data.inside_plate_data?.k_mod}
        kModOutside={maxUtilizationCheck.resistance_data.outside_plate_data?.k_mod}
      />
    ),
    nachweis: <ResultsTable rows={checkRows} valueHeader="Ausnutzung" />,
  })
}

export default Results2StiffeningCheckResults
